import React, { FC, MouseEventHandler } from 'react';
import Button, { ButtonProps } from '@mui/material/Button';
import { colors } from '../utils/theme';
import CircleIcon from '@mui/icons-material/Circle';

export interface RootineButtonProps extends ButtonProps {
  size?: 'small' | 'medium' | 'large';
  variation?:
    | 'primary'
    | 'outline_light'
    | 'outline_dark'
    | 'destructive'
    | 'plain';
  fullWidth?: boolean;
  className?: string;
  loading?: boolean;
  hideIcon?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  as?: 'a' | 'button';
  type?: 'submit' | 'button';
  ariaLabel?: string;
  disabled?: boolean;
  label?: string;
  url?: string;
  id?: string;
  openNewTab?: boolean;
}

const colorVariants = {
  primary: {
    normal: colors.greys.BASE_WHITE,
    pressed: colors.brand.PRIMARY_RED,
    disabled: colors.greys.DISABLED_BORDER_GREY,
  },
  outline_light: {
    normal: colors.greys.BRAND_BLACK,
    pressed: colors.greys.BASE_WHITE,
    disabled: colors.greys.DISABLED_BORDER_GREY,
  },
  outline_dark: {
    normal: colors.greens.HIGLIGHT,
    pressed: colors.greys.BRAND_BLACK,
    disabled: colors.greys.DISABLED_BORDER_GREY,
  },
  destructive: {
    normal: colors.greys.BASE_WHITE,
    pressed: colors.greys.BASE_WHITE,
    disabled: colors.greys.DISABLED_BORDER_GREY,
  },
  plain: {
    normal: colors.blues.HIGHLIGHT,
    pressed: colors.blues.MID,
    disabled: colors.greys.DISABLED_BORDER_GREY,
  },
};

const RootineButton: FC<RootineButtonProps> = (props) => {
  const {
    size = 'large',
    variation = 'primary',
    fullWidth = false,
    className,
    loading = false,
    hideIcon = false,
    onClick,
    as = 'button',
    type = 'button',
    ariaLabel,
    disabled = false,
    label,
    url,
    id,
    openNewTab = false,
    ...restProps
  } = props;
  const small = size === 'small';
  const medium = size === 'medium';
  const outline_light = variation === 'outline_light';
  const outline_dark = variation === 'outline_dark';
  const destructive = variation === 'destructive';
  const plain = variation === 'plain';

  let variant: 'contained' | 'outlined' | 'text';
  switch (variation) {
    case 'primary':
    case 'destructive':
      variant = 'contained';
      break;
    case 'outline_light':
    case 'outline_dark':
      variant = 'outlined';
      break;
    case 'plain':
      variant = 'text';
      break;
    default:
      variant = 'contained';
      break;
  }

  return (
    <Button
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      onClick={onClick}
      href={as === 'a' ? url : undefined}
      component={as}
      aria-label={ariaLabel}
      type={as === 'button' ? type : undefined}
      id={id}
      target={openNewTab ? '_blank' : ''}
      className={className}
      startIcon={
        !hideIcon && (
          <CircleIcon
            sx={{
              width: small || medium ? 14 : 18,
              height: small || medium ? 14 : 18,
            }}
          />
        )
      }
      disableRipple
      disableElevation
      {...restProps}
      sx={{
        borderRadius: 1.25,
        backgroundColor: colors.brand.PRIMARY_RED,
        color: colors.greys.BASE_WHITE,
        padding: '12px, 24px',
        fontSize: 20,
        '&:hover': {
          background: colors.brand.DARK_RED,
          color: colors.greys.BASE_WHITE,
        },
        '&:focus-visible': {
          border: `4px solid ${colors.blues.HIGHLIGHT}`,
        },
        '&:active': {
          color: colors.greys.BASE_WHITE,
          background: colors.brand.DARK_RED,
          '.MuiButton-startIcon': {
            color: colorVariants[variation]?.pressed,
          },
        },
        '&:disabled': {
          background: colors.greys.DISABLED_GREY,
          border: `1px solid ${colors.greys.DISABLED_BORDER_GREY}`,
          color: colors.greys.DISABLED_BORDER_GREY,
        },
        // Variations
        ...(outline_light && {
          color: colors.greys.BRAND_BLACK,
          background: colors.greys.BASE_WHITE,
          border: `1px solid ${colors.greys.DISABLED_BORDER_GREY}`,
          '&:hover': {
            background: colors.greens.GRAY,
            color: colors.greys.BRAND_BLACK,
          },
        }),
        ...(outline_dark && {
          background: colors.greys.BRAND_BLACK,
          border: `1px solid ${colors.greens.HIGLIGHT}`,
          color: colors.greens.HIGLIGHT,
          '&:hover': {
            background: colors.greys.GREY_BROWN,
            color: colors.greens.HIGLIGHT,
          },
          '&:active': {
            background: colors.greens.MID,
            color: colors.greys.BRAND_BLACK,
            border: `1px solid ${colors.greys.DISABLED_BORDER_GREY}`,
          },
        }),
        ...(destructive && {
          background: colors.reds.DESTRUCTIVE,
          border: `1px solid ${colors.greys.BRAND_BLACK}`,
          color: colors.greys.BASE_WHITE,
          '&:hover': {
            background: colors.reds.MEDIUM,
            color: colors.greys.BASE_WHITE,
          },
          '&:active': {
            background: colors.reds.DARK,
          },
        }),
        ...(plain && {
          background: 'transparent',
          border: 'none',
          color: colors.blues.HIGHLIGHT,
          px: 0,
          '&:hover': {
            textDecoration: 'underline',
            background: 'transparent',
            color: colors.blues.HIGHLIGHT,
          },
          '&:active': {
            background: 'transparent',
            color: colors.blues.MID,
          },
          '&:focus-visible': {
            border: `4px solid ${colors.blues.HIGHLIGHT}`,
            px: 1,
          },
          '&:disabled': {
            color: colors.greys.DISABLED_BORDER_GREY,
          },
        }),
        ...(loading && {
          background: colors.greys.LOADING_BG_GREY,
          border: `1px solid ${colors.greys.DISABLED_BORDER_GREY}`,
        }),
        // Sizes
        ...(small && {
          padding: '4px, 12px',
          fontSize: 16,
        }),
        ...(medium && {
          padding: '8px, 16px',
          fontSize: 16,
        }),
        '.MuiButton-startIcon': {
          marginRight: 1.25,
        },
        ...restProps.sx,
      }}
    >
      {loading ? (
        <>
          <CircleIcon
            sx={{
              mr: -0.4,
              color: colors.greys.GREY_600,
              width: 22,
              height: 22,
            }}
          />
          <CircleIcon
            sx={{
              ml: -0.4,
              color: colors.greys.GREY_600,
              width: 22,
              height: 22,
            }}
          />
        </>
      ) : (
        label
      )}
    </Button>
  );
};

export default RootineButton;
